import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/lib/data';
import { DEFAULT_NEWS } from '@/lib/defaults';
import { SEO, createBreadcrumbSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { TimelineSkeleton } from '@/components/Skeletons';
import { OptimizedImage } from '@/components/OptimizedImage';
import { formatDateTR } from '@/lib/helpers';
import { Calendar, MapPin, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const NEWS_PER_PAGE = 9;

export default function NewsPage() {
  const [news, setNews] = useState(DEFAULT_NEWS);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    axios.get(`${API}/news?page=${page}&limit=${NEWS_PER_PAGE}`)
      .then(r => {
        const data = r.data;
        setNews(data.items || data);
        if (data.pages) setTotalPages(data.pages);
      })
      .catch(console.error);
  }, [page]);

  const types = ['all', ...new Set(news.map(n => n.type))];
  const filtered = news.filter(n => filter === 'all' || n.type === filter);

  return (
    <div data-testid="news-page">
      <SEO title="Haberler & Kongreler" description="Prof. Dr. Nilüfer Yıldırım - Fonksiyonel tıp alanında güncel gelişmeler, kongre katılımları, akademik çalışmalar ve haberler." schema={createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'Haberler' }])} />
      <section className="bg-[#F5F3EF] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-semibold tracking-[-0.02em] mb-4">Haberler & Kongreler</h1>
          <p className="text-[22px] text-[#5F6B76] max-w-xl">Güncel gelişmeler, kongre katılımları ve haberler.</p>
        </div>
      </section>
      <Breadcrumb items={[{ label: 'Haberler', href: '/haberler', current: true }]} />

      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3 mb-8" data-testid="timeline-filter-year-select">
          <Filter size={16} className="text-[#5F6B76]" />
          {types.map(t => (
            <button key={t} onClick={() => setFilter(t)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${filter === t ? 'bg-[#1F6E7A] text-white' : 'bg-[#F5F3EF] text-[#5F6B76] hover:bg-[#E7E5E4]'}`}>
              {t === 'all' ? 'Tümü' : t}
            </button>
          ))}
        </div>

        {(
            <div className="space-y-6">
              {filtered.map((n, i) => (
                <div key={n.id} className="flex gap-4 items-start" data-testid="timeline-entry-card">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-[#1F6E7A] border-2 border-white shadow-sm" />
                    {i < filtered.length - 1 && <div className="w-0.5 flex-1 min-h-[60px] bg-[#E7E5E4]" />}
                  </div>
                  <div className="flex-1 bg-white rounded-[20px] border border-[#E7E5E4] overflow-hidden -mt-1">
                    {n.image && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <OptimizedImage src={n.image} alt={n.title} className="w-full h-full object-cover" sizes="(max-width: 768px) 100vw, 640px" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-2 py-0.5 rounded-full">{n.type}</span>
                        <span className="text-xs text-[#5F6B76] flex items-center gap-1"><Calendar size={12} /> {formatDateTR(n.date)}</span>
                        {n.city && <span className="text-xs text-[#5F6B76] flex items-center gap-1"><MapPin size={12} /> {n.city}</span>}
                      </div>
                      <h3 className="font-heading font-semibold text-[22px] mb-2">{n.title}</h3>
                      <p className="text-[14px] text-[#5F6B76] leading-relaxed">{n.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && filter === 'all' && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="news-pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="w-9 h-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] hover:bg-[#F5F3EF] disabled:opacity-30 transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-9 h-9 rounded-full text-xs font-medium transition-colors ${p === page ? 'bg-[#1F6E7A] text-white' : 'border border-[#E7E5E4] text-[#5F6B76] hover:bg-[#F5F3EF]'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="w-9 h-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] hover:bg-[#F5F3EF] disabled:opacity-30 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
